import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { lazy, Suspense } from 'react'
import { RouterProvider } from 'react-aria-components'
import { createBrowserRouter, Navigate, Outlet, RouterProvider as ReactRouterProvider } from 'react-router-dom'
import { useHref, useNavigate } from 'react-router-dom'

import StandaloneLoadingSpinner from '@/lib/components/StandaloneLoadingSpinner'
import StoreRoleGuard from '@/lib/provider/StoreRoleGuard'
import AccountRoute from '@routes/Account/AccountRoute'
import ChangeLanguageRoute from '@routes/Account/ChangeLanguageRoute'
import ChangeScanRoute from '@routes/Account/ChangeScanRoute'
import ChangeStoreRoute from '@routes/Account/ChangeStoreRoute'
import AdministrationRoute from '@routes/Administration/AdministrationRoute'
import AnalyticsLandingRoute from '@routes/Analytics/AnalyticsLandingRoute'
import ComponentsRoute from '@routes/Components/ComponentsRoute'
import FakePrincipalRoute from '@routes/FakePrincipal/FakePrincipalRoute'
import MapInvestigator from '@routes/MapInvestigator/MapInvestigator'
import PanoramaRoute from '@routes/Panorama/PanoramaRoute'
import { RouterErrorBoundary } from '@routes/RouterErrorBoundary'
import StoreManagementRoute from '@routes/StoreManagement/StoreManagementRoute'
import TodoListRoute from '@routes/TodoList/TodoListRoute'
import WorkflowRoute from '@routes/Workflow/WorkflowRoute'
import { isProdMode } from '@utils/env.ts'
import { routesFactory } from '@utils/routes.ts'

import { DefaultLayout, FocusedLayout, ResponsiveLayout, RootLayout } from './Layouts'
import ScanSelectGuard from './lib/provider/ScanSelectGuard'
import StoreSelectGuard from './lib/provider/StoreSelectGuard'
import SystemRoleGuard from './lib/provider/SystemRoleGuard'
import ProductFinderRoute from './routes/ProductFinder/ProductFinderRoute'
import StoreMapRoute from './routes/StoreMap/StoreMapRoute'

import type { NavigateOptions } from 'react-router-dom'

declare module 'react-aria-components' {
    interface RouterConfig {
        routerOptions: NavigateOptions
    }
}

// Wrapper component that provides React Aria router context
function AriaRouterWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()

  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      {children}
    </RouterProvider>
  )
}

// Lazy-loaded components
const LazyScanReportRoute = lazy(() => import('@routes/Analytics/scanReport/ScanReportRoute'))

const router = createBrowserRouter([
  {
    path: '/',
    element: <AriaRouterWrapper><RootLayout /></AriaRouterWrapper>,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to={routesFactory.storeSelection()} replace />,
      },
      {
        path: 'administration',
        element: <SystemRoleGuard allowedRoles={['ADMIN']}><AdministrationRoute /></SystemRoleGuard>,
      },
      {
        path: 'workflows',
        element: <WorkflowRoute />,
      },
      {
        path: 'store',
        element: <StoreSelectGuard />,
        children: [
          {
            path: ':storeId',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to="scan" replace />,
              },
              {
                path: 'scan',
                element: <ScanSelectGuard />,
                children: [
                  {
                    path: ':scanId',
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="product-finder" replace />,
                      },
                      // DefaultLayout group
                      {
                        element: <DefaultLayout />,
                        children: [
                          {
                            path: 'product-finder',
                            element: <ProductFinderRoute />,
                          },
                          {
                            path: 'out-of-shelf-list',
                            element: <TodoListRoute />,
                          },
                          {
                            path: 'store-management',
                            element: (
                              <StoreRoleGuard allowedRoles={['STORE_MANAGER']}>
                                <StoreManagementRoute />
                              </StoreRoleGuard>
                            ),
                          },
                          {
                            path: 'account',
                            element: <AccountRoute />,
                          },
                          {
                            path: 'analytics',
                            element: <SystemRoleGuard allowedRoles={['ADMIN']}><Outlet /></SystemRoleGuard>,
                            children: [
                              {
                                index: true,
                                element: <AnalyticsLandingRoute />,
                              },
                              {
                                path: 'shelf-scan-report',
                                element: (
                                  <Suspense fallback={<StandaloneLoadingSpinner />}>
                                    <LazyScanReportRoute />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                          {
                            path: 'map-investigator',
                            element: <MapInvestigator />,
                          },
                        ],
                      },
                      // FocusedLayout group
                      {
                        element: <FocusedLayout />,
                        children: [
                          {
                            path: 'account/change-store',
                            element: <ChangeStoreRoute />,
                          },
                          {
                            path: 'account/change-scan',
                            element: <ChangeScanRoute />,
                          },
                          {
                            path: 'account/change-language',
                            element: <ChangeLanguageRoute />,
                          },
                        ],
                      },
                      // ResponsiveLayout group
                      {
                        element: <ResponsiveLayout />,
                        children: [
                          {
                            path: 'store-map',
                            element: <StoreMapRoute />,
                          },
                        ],
                      },
                      {
                        path: 'panorama',
                        element: <ResponsiveLayout />,
                        children: [
                          {
                            index: true,
                            element: <PanoramaRoute />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      !isProdMode ? {
        path: 'components',
        element: <ComponentsRoute />,
      } : null,

      !isProdMode ? {
        path: 'fake-principal',
        element: <FakePrincipalRoute />,
      } : null,
    ].filter(r => r !== null),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])

export default function Router() {
  return <NuqsAdapter><ReactRouterProvider router={router} /></NuqsAdapter>
}
